import isServer from '@framework/router/Next/isServer';
import config from '@creditflowresearch/config';
import { CacheEntry, buildCacheKey, getCached, setCache, cachePromiseWithEviction } from './helpers';

const CLIENT_CACHE_KEY = `__${ config.code }__clientCache__`;
const CLIENT_SCOPE = 'client';
const INFINITE_TTL = Infinity;

/**
 * Client-side in-memory cache (per class instance, no TTL — lives until instance is GC'd).
 * Server-side calls pass through without caching.
 */
export function withClientCache(_target: object, key: string, descriptor: PropertyDescriptor): PropertyDescriptor {
  if (typeof descriptor?.value !== 'function') {
    return descriptor;
  }

  const originalFn = descriptor.value;

  descriptor.value = function decorator (...args: unknown[]) {
    if (isServer()) {
      return originalFn.apply(this, args);
    }

    if (!this[CLIENT_CACHE_KEY]) {
      this[CLIENT_CACHE_KEY] = new Map<string, CacheEntry>();
    }

    const instanceCache: Map<string, CacheEntry> = this[CLIENT_CACHE_KEY];
    const cacheKey = buildCacheKey(CLIENT_SCOPE, key, args);
    const cached = getCached(instanceCache, cacheKey);

    if (cached !== undefined) {
      return cached;
    }

    const result = originalFn.apply(this, args);

    if (result instanceof Promise) {
      const promise = cachePromiseWithEviction(result, instanceCache, cacheKey);

      setCache(instanceCache, cacheKey, promise, INFINITE_TTL);

      return promise;
    }

    setCache(instanceCache, cacheKey, result, INFINITE_TTL);

    return result;
  };

  return descriptor;
}
